import { useState } from 'react';
import { OfferResponse } from '../../types/offers';
import { discountLabel, formatAed } from './offerReviewModel';

type Locale = 'en' | 'ar';

interface OfferPhonePreviewProps {
  offer: OfferResponse;
  garageName: string;
}

/**
 * Spec 062 — the customer hero card as the app draws it (AC-10, AC-19): the partner's banner under a
 * navy scrim, the backend badge, title, lead price line and garage name, then the covered services.
 * Self-contained to the admin portal; the partner portal keeps its own copy. Arabic falls back to the
 * EN title and EN banner, prices stay LTR.
 */
export default function OfferPhonePreview({ offer, garageName }: OfferPhonePreviewProps) {
  const [locale, setLocale] = useState<Locale>('en');
  const [failedUrl, setFailedUrl] = useState<string | null>(null);

  const isAr = locale === 'ar';
  const bannerUrl = (isAr ? offer.bannerAr?.downloadUrl : null) || offer.banner?.downloadUrl || null;
  const showImage = !!bannerUrl && failedUrl !== bannerUrl;
  const title = (isAr ? offer.titleAr : null) || offer.titleEn;
  const subtitle = (isAr ? offer.subtitleAr : null) || (isAr && offer.titleAr ? null : offer.subtitleEn);
  // The server orders items like the app feed (bookable, largest saving, name): the first is the app's lead.
  const lead = offer.items[0];
  const usesFallback = isAr && (!offer.titleAr || !offer.bannerAr?.downloadUrl);

  return (
    <div className="offer-preview">
      <div className="offer-preview__toggle" role="group" aria-label="Preview language">
        {(['en', 'ar'] as Locale[]).map((l) => (
          <button
            key={l}
            type="button"
            className={`offer-preview__toggle-btn${locale === l ? ' offer-preview__toggle-btn--active' : ''}`}
            aria-pressed={locale === l}
            onClick={() => setLocale(l)}
          >
            {l === 'en' ? 'EN' : 'AR'}
          </button>
        ))}
      </div>

      <div className="offer-phone" data-testid="offer-phone-preview">
        <div className="offer-phone__notch" />
        <div className="offer-phone__screen" dir={isAr ? 'rtl' : 'ltr'} lang={locale}>
          <div className="offer-card">
            {showImage ? (
              <img
                className="offer-card__banner"
                src={bannerUrl!}
                alt=""
                onError={() => setFailedUrl(bannerUrl)}
              />
            ) : (
              <div className="offer-card__banner offer-card__banner--fallback" data-testid="offer-banner-fallback" />
            )}
            <div className="offer-card__scrim" />
            <div className="offer-card__content">
              <span className="offer-card__badge" dir="ltr">{discountLabel(offer)}</span>
              <div className="offer-card__title">{title}</div>
              {subtitle && <div className="offer-card__subtitle">{subtitle}</div>}
              {lead && (
                <div className="offer-card__price" dir="ltr">
                  <s>{formatAed(lead.listPrice)}</s> → <strong>{formatAed(lead.discountedPrice)}</strong>
                </div>
              )}
              <div className="offer-card__garage">{garageName}</div>
            </div>
          </div>

          <ul className="offer-phone__items">
            {offer.items.map((item) => (
              <li key={item.offeringId} className="offer-phone__item">
                <span className="offer-phone__item-name">{item.name}</span>
                <span className="offer-phone__item-price" dir="ltr">
                  <s>{formatAed(item.listPrice)}</s> <strong>{formatAed(item.discountedPrice)}</strong>
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
      {usesFallback && (
        <p className="offer-preview__note">
          {!offer.titleAr && 'No Arabic title: the app shows the English title. '}
          {!offer.bannerAr?.downloadUrl && 'No Arabic banner: the app shows the English image.'}
        </p>
      )}
    </div>
  );
}
